/*  ========================================================================
    # Components - Lazy Image
    ========================================================================  */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import LazySource from './LazySource';

const LazyImage = ({
  figureClass,
  imageClass,
  children,
  defaultUrl,
  placeholderUrl,
  alt,
  onClick,
  dataOffset,
  preload,
  customRootMargin = '300px'
}) => {
  const imageElmRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const defaultPlaceholder = placeholderUrl
    ? placeholderUrl
    : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPs+Q8AAh0BjeU/5JAAAAAASUVORK5CYII=';

  /**
   *
   */
  const sourceElements = (items, isVisible) => {
    if (!items) {
      return null;
    } else {
      return Array.isArray(items)
        ? items.map((child, index) =>
          React.cloneElement(child, { key: index, visible: preload || isVisible })
        )
        : React.cloneElement(items, { visible: preload || isVisible });
    }
  };

  /**
   *
   */
  const { ref, inView } = useInView({
    rootMargin: customRootMargin,
    threshold: 0,
    triggerOnce: true
  });

  /**
   *
   */
  useEffect(() => {
    imageElmRef.current?.addEventListener('load', setImageLoaded(true));
    return () => imageElmRef.current?.removeEventListener('load', setImageLoaded(true));
  }, [imageElmRef.current]);

  return (
    <figure
      ref={ref}
      onClick={onClick}
      data-offset={dataOffset ? dataOffset : 0}
      style={!imageLoaded ? { backgroundImage: `url(${defaultPlaceholder})` } : {}}
      className={[
        'c-lazy-image',
        (inView && imageLoaded) || preload ? '' : 'c-lazy-image--pending',
        !inView && !imageLoaded && !placeholderUrl ? 'c-lazy-image--loading' : '',
        figureClass
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='c-lazy-image__loader' />
      <picture>
        {sourceElements(children, inView)}
        <img
          className={['c-lazy-image__img', imageClass].filter(Boolean).join(' ')}
          src={(inView && imageLoaded) || preload ? defaultUrl : defaultPlaceholder}
          alt={alt}
          ref={imageElmRef}
        />
      </picture>
    </figure>
  );
};

LazyImage.propTypes = {
  figureClass: PropTypes.string,
  imageClass: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.objectOf(LazySource), PropTypes.arrayOf(LazySource)]),
  defaultUrl: PropTypes.string,
  placeholderUrl: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  dataOffset: PropTypes.number,
  preload: PropTypes.bool,
  customRootMargin: PropTypes.string
};

export default LazyImage;
